// ABOUTME: Create menu dropdown and bottom sheet
// ABOUTME: Shows options for creating a new Post or Link

"use client"

import { useEffect, useRef } from "react"
import Link from "next/link"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare, faLink, faXmark } from "@fortawesome/free-solid-svg-icons"

interface CreateMenuProps {
  isOpen: boolean
  onClose: () => void
  onOpenAddLink: () => void
  isMobile: boolean
  showOnDesktop?: boolean
}

export function CreateMenu({ isOpen, onClose, onOpenAddLink, isMobile, showOnDesktop = false }: CreateMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    if (isOpen) {
      window.addEventListener("keydown", handleEscape)
      return () => window.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  // Close on click outside (desktop dropdown only)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isMobile && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    if (isOpen && !isMobile) {
      // Delay to prevent immediate close from the same click
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside)
      }, 0)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, isMobile, onClose])

  if (!isOpen) return null

  // Desktop: Simple dropdown
  if (!isMobile) {
    return (
      <div
        ref={menuRef}
        className="hidden md:block absolute top-full right-0 mt-2 w-48 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg overflow-hidden z-50"
      >
        <Link
          href="/new"
          onClick={onClose}
          className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
        >
          <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium">Post</span>
        </Link>
        <button
          onClick={onOpenAddLink}
          className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors border-t border-gray-100 dark:border-neutral-700"
        >
          <FontAwesomeIcon icon={faLink} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium">Link</span>
        </button>
      </div>
    )
  }

  // Mobile: Bottom sheet (also shown on desktop when showOnDesktop is true)
  const hideClass = showOnDesktop ? "" : "md:hidden"

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${hideClass} fixed inset-0 bg-black/50 z-50 transition-opacity`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div className={`${hideClass} fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 rounded-t-2xl z-50 animate-slide-up`}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 dark:bg-neutral-700 rounded-full" />
        </div>

        {/* Options */}
        <div className="px-4 pb-8 pt-2 space-y-2">
          <Link
            href="/new"
            onClick={onClose}
            className="flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-neutral-800 rounded-full">
              <FontAwesomeIcon icon={faPenToSquare} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">Post</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Write a full blog post</div>
            </div>
          </Link>

          <button
            onClick={onOpenAddLink}
            className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-left"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-neutral-800 rounded-full">
              <FontAwesomeIcon icon={faLink} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">Link</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Share a URL with your thoughts</div>
            </div>
          </button>

          {/* Cancel button */}
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            <span className="text-sm font-medium">Cancel</span>
          </button>
        </div>
      </div>
    </>
  )
}
